import { Typography } from '@mui/material';
import Papa from 'papaparse';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import { checkSIIDocumentForUploadingInvoices } from '../../../../../../../../../lib/api/sii';
import CenteredCircularProgress from '../../../../../../../../../lib/designSystem/CircularProgress';
import Uploader from '../../../../../../../../../lib/designSystem/Uploader';

const normalizeDateInput = (value: any): string => {
  if (!value && value !== 0) return '';

  const pad = (part: number) => String(part).padStart(2, '0');

  if (value instanceof Date && !isNaN(value.getTime())) {
    const yyyy = value.getFullYear();
    const mm = pad(value.getMonth() + 1);
    const dd = pad(value.getDate());
    return `${yyyy}/${mm}/${dd}`;
  }

  if (typeof value === 'string') {
    const isoCandidate = new Date(value);
    if (!isNaN(isoCandidate.getTime())) {
      const yyyy = isoCandidate.getFullYear();
      const mm = pad(isoCandidate.getMonth() + 1);
      const dd = pad(isoCandidate.getDate());
      return `${yyyy}/${mm}/${dd}`;
    }
  }

  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      const yyyy = parsed.y;
      const mm = pad(parsed.m);
      const dd = pad(parsed.d);
      return `${yyyy}/${mm}/${dd}`;
    }
  }

  const raw = String(value).trim();
  if (!raw) return '';

  const normalized = raw.replace(/\./g, '/').replace(/-/g, '/');
  const parts = normalized.split('/').filter(Boolean);
  if (parts.length !== 3) return '';

  let [day, month, year] = parts.map((part) => part.trim());

  if (day.length === 4 && year.length <= 2) {
    // Handle yyyy-dd-mm or similar inputs just in case
    const swap = day;
    day = year;
    year = swap;
  }

  if (year.length === 2) {
    year = `20${year}`;
  }

  const dayNum = Number(day);
  const monthNum = Number(month);
  const yearNum = Number(year);

  if (Number.isNaN(dayNum) || Number.isNaN(monthNum) || Number.isNaN(yearNum)) {
    return '';
  }

  return `${yearNum}/${pad(monthNum)}/${pad(dayNum)}`;
};

const sanitizeAmount = (value: any) => {
  if (typeof value === 'number') return value.toString();
  if (!value) return '';
  return String(value).replace(/\./g, '').replace(/,/g, '');
};

const AddInvoicesCSV = (props: any) => {
  const { business, setInvoices } = props;
  const dispatch = useDispatch();
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleFileChange = async (event: any) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    setUploadedFileName(uploadedFile.name);

    let parsedFile: any[] = [];

    if (uploadedFile.name.endsWith('.csv')) {
      parsedFile = await parseCSV(uploadedFile);
    } else if (
      uploadedFile.name.endsWith('.xlsx') ||
      uploadedFile.name.endsWith('.xls')
    ) {
      parsedFile = await parseXLSX(uploadedFile);
    } else {
      alert('Formato de archivo no soportado. Solo .csv y .xlsx');
      return;
    }

    const formattedFile = formatedCSV(parsedFile);
    await validateDocuments(formattedFile);
  };

  const formatedCSV = (parsedFile: any[]) => {
    return parsedFile.map((invoice: any) => {
      const code = invoice['Codigo'] ? invoice['Codigo'] : '33';
      const issuedDate =
        normalizeDateInput(invoice['Fecha Recepcion']) ||
        invoice['Fecha Recepcion'];
      const dueDate = normalizeDateInput(invoice['Vencimiento']);
      return {
        folio: invoice.Folio,
        businessIdentifier: business.identifier,
        stakeholderName: invoice['Razon Social'],
        stakeholderIdentifier: invoice['Rut deudor'],
        issuedDate,
        dueDate: dueDate || undefined,
        amount: sanitizeAmount(invoice['Monto total']),
        type: code === '33' ? 'electronic-invoice' : 'electronic-invoice-exent',
      };
    });
  };

  const validateDocuments = async (invoices: any[]) => {
    try {
      setLoading(true);
      const newInvoices = invoices.map((invoice: any) => {
        const [yyyy, mm, dd] = invoice.issuedDate.split('/');
        const date = `${dd}${mm}${yyyy}`;
        return {
          ...invoice,
          issuedDate: date,
        };
      });
      const response = await checkSIIDocumentForUploadingInvoices(dispatch, {
        invoices: newInvoices,
      });

      if (response.length) {
        const failedInvoices = response.filter(
          (invoice: any) => !invoice.valid,
        );

        const validInvoicesFolios = response
          .filter((invoice: any) => invoice.valid)
          .map((invoice: any) => invoice.folio);

        const validInvoices = invoices.filter((invoice: any) =>
          validInvoicesFolios.includes(invoice.folio),
        );

        if (failedInvoices.length) {
          setInvoices(validInvoices);

          alert(
            `Los siguientes documentos no son válidos: ${failedInvoices
              .map((invoice: any) => invoice.folio)
              .join(', ')}`,
          );
          return false;
        } else {
          setInvoices(validInvoices);
          return true;
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const parseCSV = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => resolve(result.data),
        error: (error) => reject(error),
      });
    });
  };

  const parseXLSX = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const json = XLSX.utils.sheet_to_json(worksheet, {
          defval: '',
          raw: false, // permite que fechas se entreguen como strings si es posible
        });

        const parsed = json.map((row: any) => {
          const fechaRecepcion =
            normalizeDateInput(row['Fecha Recepcion']) ||
            row['Fecha Recepcion'];
          const vencimiento = normalizeDateInput(row['Vencimiento']);

          return {
            ...row,
            'Fecha Recepcion': fechaRecepcion,
            Vencimiento: vencimiento,
            'Monto total': sanitizeAmount(row['Monto total']),
          };
        });

        resolve(parsed);
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  };

  const downloadTemplate = () => {
    // build template data
    const template = [
      {
        Folio: '123',
        'Razon Social': 'Tech Spa',
        'Rut deudor': '11111111-1',
        'Fecha Recepcion': '14/08/2025',
        Vencimiento: '14/09/2025',
        'Monto total': '100000',
        Codigo: '33',
      },
    ];

    // build xlsx workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(template);

    // build columns
    const columns = [
      [
        'Folio',
        'Razon Social',
        'Rut deudor',
        'Fecha Recepcion',
        'Vencimiento',
        'Monto total',
        'Codigo',
      ],
    ];
    XLSX.utils.sheet_add_aoa(worksheet, columns, { origin: 'A1' });

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'template.xlsx');
  };

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div style={{ padding: '10px 0px' }}>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <>
          <Typography variant="body1" style={{ paddingBottom: '10px' }}>
            Agrega el archivo xlsx con las facturas que quieres ingresar
          </Typography>
          <Uploader
            handleFileChange={handleFileChange}
            buttonText="Agregar XLSX"
            accept=".xlsx,.xls"
            uploadedFileName={uploadedFileName}
          />
          <Typography
            variant="body1"
            textAlign="center"
            style={{
              paddingTop: '10px',
              cursor: 'pointer',
              color: 'blue',
              fontWeight: 'bold',
            }}
            onClick={downloadTemplate}
          >
            Descargar template
          </Typography>
        </>
      )}
    </div>
  );
};

export default AddInvoicesCSV;
